import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import parse from "html-react-parser";
import {
    Box,
    Link,
    Typography,
    Stack,
    Divider,
    IconButton,
} from "@mui/material";
import {
    Mail,
    Phone,
    Linkedin,
    MapPin,
    Edit3,
} from "lucide-react";
import { toTitleCase } from "../utils";
import { resumeBuilderSelector } from "../ResumeBuilderActions/selector";
import ResumeContext from "../Resumecontext";
import { setCurrentSection } from "../ResumeBuilderActions/actions";

const Template2 = ({ accentColor = "#004aad", forPDF = false, addedSkills = [] }) => {
    const dispatch = useDispatch();
    const context = useContext(ResumeContext);
    const { currentSection, resumeFullDetails, projects } = useSelector(resumeBuilderSelector);

    const {
        resumedetails = {},
        projectdetails = [],
        educationdetails = [],
        certificatedetails = [],
    } = resumeFullDetails || {};

    const [displayedProjects, setDisplayedProjects] = useState([]);

    useEffect(() => {
        if (currentSection?.section === "Project") {
            setDisplayedProjects(projects);
        } else {
            setDisplayedProjects(projectdetails);
        }
    }, [currentSection, projects, projectdetails]);

    const handleEditClick = (section, id) => {
        dispatch(setCurrentSection(section, id));
    };

    useEffect(() => {
        const style = document.createElement("style");
        style.innerHTML = `
      @media print {
        @page { margin: 1in; size: A4; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date + "-01");
        return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    };

    const skills = [
        ...new Set(
            [resumedetails?.ai_skills, context.aiSkills]
                .filter(Boolean)
                .join(", ")
                .split(",")
                .map((skill) => skill.trim())
                .filter(Boolean)
        ),
    ];

    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
                bgcolor: "#fff",
                color: "#000",
                display: "flex",
                flexDirection: "row",
                fontFamily: "'Inter', sans-serif",
                minHeight: '92vh',
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                overflow: "hidden"
            }}
        >
            {/* ===== SIDEBAR ===== */}
            <Box sx={{
                width: "28%",
                bgcolor: "#f4f7f6",
                borderRight: `4px solid ${accentColor}`,
                p: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 3
            }}>
                {/* Contact Info */}
                <Box>
                    <Typography sx={{ fontWeight: 800, color: accentColor, mb: 1.5, textTransform: "uppercase", letterSpacing: 1.2, fontSize: "0.7rem" }}>Contact</Typography>
                    <Stack spacing={1.5}>
                        {resumedetails.email && (
                            <Stack direction="row" spacing={1} alignItems="flex-start" onClick={() =>
                                window.open(
                                    `https://mail.google.com/mail/?view=cm&fs=1&to=${resumedetails.email}`,
                                    "_blank"
                                )
                            } sx={{ cursor: 'pointer' }}>
                                <Mail size={12} color={accentColor} style={{ marginTop: 2 }} />
                                <Typography sx={{ fontSize: "0.75rem", wordBreak: "break-all", fontWeight: 500 }}>{resumedetails.email.toLowerCase()}</Typography>
                            </Stack>
                        )}
                        {resumedetails.home_phone && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Phone size={12} color={accentColor} />
                                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>{resumedetails.home_phone}</Typography>
                            </Stack>
                        )}
                        {resumedetails.linkedin_url && (
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Linkedin size={12} color={accentColor} style={{ marginTop: 2 }} />
                                <Link href={resumedetails.linkedin_url} target="_blank" sx={{ fontSize: "0.75rem", color: "inherit", textDecoration: "none", fontWeight: 500, wordBreak: "break-all" }}>LinkedIn</Link>
                            </Stack>
                        )}
                        {resumedetails.cityname && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <MapPin size={12} color={accentColor} />
                                <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>{`${resumedetails.cityname}, ${resumedetails.statename}`}</Typography>
                            </Stack>
                        )}
                    </Stack>
                </Box>

                {educationdetails?.filter(edu =>
                    (edu.edu_course && edu.edu_course !== "null" && edu.edu_course !== "undefined" && edu.edu_course.trim() !== "") ||
                    (edu.edu_university && edu.edu_university !== "null" && edu.edu_university !== "undefined" && edu.edu_university.trim() !== "")
                ).length > 0 && (
                        <Box>
                            <Typography sx={{ fontWeight: 800, color: accentColor, mb: 1.5, textTransform: "uppercase", letterSpacing: 1.2, fontSize: "0.7rem" }}>Education</Typography>
                            {educationdetails
                                .filter(edu =>
                                    (edu.edu_course && edu.edu_course !== "null" && edu.edu_course !== "undefined" && edu.edu_course.trim() !== "") ||
                                    (edu.edu_university && edu.edu_university !== "null" && edu.edu_university !== "undefined" && edu.edu_university.trim() !== "")
                                )
                                .map((edu, i) => (
                                    <Box key={i} mb={1.5}>
                                        {edu.edu_course && edu.edu_course !== "null" && edu.edu_course !== "undefined" && (
                                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#2d3436" }}>{toTitleCase(edu.edu_course)}</Typography>
                                        )}
                                        {edu.edu_university && edu.edu_university !== "null" && edu.edu_university !== "undefined" && (
                                            <Typography sx={{ fontSize: "0.75rem", color: "#636e72" }}>{toTitleCase(edu.edu_university)}</Typography>
                                        )}
                                        {edu.edu_passing_year && edu.edu_passing_year !== "null" && edu.edu_passing_year !== "undefined" && (
                                            <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: accentColor }}>{edu.edu_passing_year}</Typography>
                                        )}
                                    </Box>
                                ))}
                        </Box>
                    )}

                {certificatedetails?.filter(cert =>
                    cert.cer_name &&
                    cert.cer_name !== "null" &&
                    cert.cer_name !== "undefined" &&
                    cert.cer_name.trim() !== ""
                ).length > 0 && (
                        <Box>
                            <Typography sx={{ fontWeight: 800, color: accentColor, mb: 1.5, textTransform: "uppercase", letterSpacing: 1.2, fontSize: "0.7rem" }}>Certifications</Typography>
                            {certificatedetails
                                .filter(cert =>
                                    cert.cer_name &&
                                    cert.cer_name !== "null" &&
                                    cert.cer_name !== "undefined" &&
                                    cert.cer_name.trim() !== ""
                                )
                                .map((cert, i) => (
                                    <Box key={i} mb={1}>
                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#2d3436" }}>{toTitleCase(cert.cer_name)}</Typography>
                                        {cert.cer_year && cert.cer_year !== "null" && cert.cer_year !== "undefined" && (
                                            <Typography sx={{ fontSize: "0.7rem", color: accentColor, fontWeight: 600 }}>{cert.cer_year}</Typography>
                                        )}
                                    </Box>
                                ))}
                        </Box>
                    )}
            </Box>

            {/* ===== MAIN CONTENT ===== */}
            <Box sx={{ flex: 1, p: 4 }}>
                {/* Header */}
                <Box mb={3.5}>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: "#2d3436", mb: 0.5, letterSpacing: -0.5 }}>{resumedetails.full_name || "Your Name"}</Typography>
                    <Typography sx={{ color: accentColor, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", fontSize: "0.85rem" }}>
                        {context.jobTitle || resumedetails.application_title || "Your Profession"}
                    </Typography>
                </Box>

                {/* Summary */}
                {(context.summary || resumedetails.summary) && (
                    <Box mb={4}>
                        <Box sx={{ borderBottom: `1.5px solid #dfe6e9`, mb: 1.5, pb: 0.5 }}>
                            <Typography sx={{ fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: 1, color: "#2d3436" }}>Summary</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.88rem", color: "#2d3436", textAlign: forPDF ? "left" : "justify", lineHeight: 1.6 }}>
                            {parse(context.summary || resumedetails.summary || "")}
                        </Typography>
                    </Box>
                )}

                {/* Skills */}
                {skills.length > 0 && (
                    <Box mb={4}>
                        <Box sx={{ borderBottom: `1.5px solid #dfe6e9`, mb: 1.5, pb: 0.5 }}>
                            <Typography sx={{ fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: 1, color: "#2d3436" }}>Technical Skills</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.88rem", color: "#2d3436", lineHeight: 1.6 }}>
                            {skills.map((skill, index) => (
                                <span key={index} style={{
                                    backgroundColor: (addedSkills?.includes(skill) && !forPDF) ? "yellow" : "transparent",
                                    padding: (addedSkills?.includes(skill) && !forPDF) ? "1px 3px" : "0",
                                    borderRadius: "2px"
                                }}>{skill}{index !== skills.length - 1 ? ", " : ""}</span>
                            ))}
                        </Typography>
                    </Box>
                )}

                {/* Work Experience */}
                {projectdetails?.length > 0 && (
                    <Box>
                        <Box sx={{ borderBottom: `1.5px solid #dfe6e9`, mb: 2, pb: 0.5 }}>
                            <Typography sx={{ fontWeight: 800, textTransform: "uppercase", fontSize: "0.8rem", letterSpacing: 1, color: "#2d3436" }}>Experience</Typography>
                        </Box>
                        {displayedProjects.map((proj, i) => (
                            <Box key={i} mb={3} sx={{
                                position: "relative",
                                "&:hover .editIcon": {
                                    visibility: "visible",
                                },
                            }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#2d3436" }}>{proj.pro_title}</Typography>
                                        {currentSection?.section === "Project" && (
                                            <IconButton
                                                className="editIcon"
                                                size="small"
                                                sx={{
                                                    visibility: "hidden",
                                                    p: 0,
                                                    color: accentColor
                                                }}
                                                onClick={() => handleEditClick("Project", proj.id)}
                                            >
                                                <Edit3 size={14} />
                                            </IconButton>
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: "0.75rem", color: accentColor, fontWeight: 700, bgcolor: `${accentColor}10`, px: 1, py: 0.2, borderRadius: "4px" }}>
                                        {proj.pro_startdate && formatDate(proj.pro_startdate)} – {proj.is_current_project === 1 ? "Present" : (proj.pro_enddate && formatDate(proj.pro_enddate))}
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, mb: 1, color: "#636e72" }}>
                                    {proj.pro_client && toTitleCase(proj.pro_client)} | {proj.pro_role && toTitleCase(proj.pro_role)}
                                </Typography>
                                {proj.pro_description && (
                                    <Box sx={{ mb: 1.5, bgcolor: "#f8f9fa", p: 1.5, borderRadius: "4px", borderLeft: `3px solid ${accentColor}` }}>
                                        <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "#2d3436", fontStyle: "italic", lineHeight: 1.5 }}>
                                            {parse(proj.pro_description)}
                                        </Typography>
                                    </Box>
                                )}
                                {proj.pro_responsibilities && (
                                    <Typography sx={{ fontSize: "0.85rem", color: "#2d3436", textAlign: forPDF ? "left" : "justify", lineHeight: 1.6 }}>
                                        {parse(proj.pro_responsibilities)}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Template2;
